/**
 * @file MapMarkerPopup.jsx
 * @description Harita marker popup bileşeni
 *
 * Zillow tarzı popup. Haritada marker'a tıklandığında
 * ilanın kısa özetini gösterir. Akıllı konumlandırma ile
 * her zaman görünür alanda kalır.
 *
 * @module widget/components/MapMarkerPopup
 */

import { ExternalLink, MapPin, X } from "lucide-react";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import { Image } from "@openai/apps-sdk-ui/components/Image";
import { formatPrice } from "../utils";

/**
 * Harita Marker Popup Bileşeni
 *
 * Marker'a tıklandığında gösterilen kompakt ilan önizlemesi.
 * _screenPosition verisini kullanarak marker'a yakın konumlanır.
 * Ekran sınırlarına göre otomatik pozisyon ayarı yapar.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object|null} props.listing - İlan verisi
 * @param {Object} [props.listing._screenPosition] - Marker ekran koordinatları
 * @param {number} props.listing._screenPosition.x - X koordinatı
 * @param {number} props.listing._screenPosition.y - Y koordinatı
 * @param {Function} props.onClose - Popup kapatma callback'i
 * @param {Function} props.onViewDetails - Detay görüntüleme callback'i
 * @param {Function} props.openExternal - Harici link açma fonksiyonu
 * @param {boolean} props.isDark - Karanlık mod
 * @param {number} [props.containerHeight=500] - Container yüksekliği (px)
 * @param {number} [props.containerWidth=800] - Container genişliği (px)
 *
 * @returns {JSX.Element|null} Popup bileşeni veya null
 *
 * @example
 * <MapMarkerPopup
 *   listing={{ id: 123, title: "3+1 Daire", price: 5000000 }}
 *   onClose={() => setPopup(null)}
 *   onViewDetails={(id) => fetchDetail(id)}
 *   openExternal={window.openai.openExternal}
 *   isDark={false}
 *   containerHeight={600}
 *   containerWidth={800}
 * />
 *
 * @remarks
 * - Popup 288px genişliğinde, ~340px yüksekliğinde
 * - Marker üstünde veya altında konumlanır (ekran yarısına göre)
 * - Kenarlara 16px margin bırakılır
 */
export function MapMarkerPopup({ listing, onClose, onViewDetails, openExternal, isDark, containerHeight = 500, containerWidth = 800 }) {
  if (!listing) return null;

  const priceLabel = formatPrice(listing?.price, listing?.currency);
  const listingId = typeof listing?.id === "number" && Number.isFinite(listing.id) ? listing.id : null;
  const imageUrl = listing?.images?.[0] || listing?.image;

  // Position popup near marker, or center if no position available
  const screenPos = listing?._screenPosition;
  const popupWidth = 288; // w-72 = 18rem = 288px
  const popupHeight = 340; // Approximate popup height
  const margin = 16; // Margin from edges

  // Calculate safe position within container bounds
  let popupStyle;
  if (screenPos && containerWidth > 0 && containerHeight > 0) {
    // Determine if popup should appear above or below marker
    const showAbove = screenPos.y > containerHeight / 2;

    // Calculate horizontal position - center on marker but keep within bounds
    let leftPos = screenPos.x;
    const minLeft = popupWidth / 2 + margin;
    const maxLeft = containerWidth - popupWidth / 2 - margin;
    leftPos = Math.max(minLeft, Math.min(leftPos, maxLeft));

    // Calculate vertical position
    let topPos;
    if (showAbove) {
      // Show above marker
      topPos = screenPos.y - margin;
    } else {
      // Show below marker
      topPos = screenPos.y + 40; // 40px below marker to clear it
    }

    // Ensure popup stays within vertical bounds
    if (showAbove) {
      topPos = Math.max(popupHeight + margin, topPos);
    } else {
      topPos = Math.min(containerHeight - popupHeight - margin, topPos);
    }

    popupStyle = {
      left: `${leftPos}px`,
      top: `${topPos}px`,
      transform: showAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
    };
  } else {
    // Fallback to center if no position available
    popupStyle = {
      right: '16px',
      top: '16px',
      transform: 'none',
    };
  }

  return (
    <div
      className={`absolute z-50 w-72 rounded-lg shadow-2xl overflow-hidden ${isDark ? "border border-white/10" : "border border-black/10"}`}
      style={{
        ...popupStyle,
        backgroundColor: isDark ? '#27272a' : '#ffffff',
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Popup'ı kapat"
        className={`absolute top-2 right-2 z-10 h-7 w-7 rounded-full flex items-center justify-center ${isDark ? "bg-black/60 text-white hover:bg-black/80" : "bg-white/80 text-black hover:bg-white"}`}
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>

      {/* Image */}
      {imageUrl ? (
        <div className="w-full h-40">
          <Image src={imageUrl} alt={listing?.title || "İlan"} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`w-full h-40 flex items-center justify-center ${isDark ? "bg-white/5" : "bg-black/5"}`}>
          <span className={`text-sm ${isDark ? "text-white/40" : "text-black/40"}`}>Görsel yok</span>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        {listing?.title && (
          <div className={`font-semibold line-clamp-2 mb-2 ${isDark ? "text-white" : "text-black"}`}>
            {listing.title}
          </div>
        )}

        {/* Price */}
        <div className={`text-xl font-bold ${isDark ? "text-white" : "text-black"}`}>
          {priceLabel || "Fiyat bilgisi yok"}
        </div>

        {/* Stats */}
        <div className={`text-sm mt-2 ${isDark ? "text-white/70" : "text-black/70"}`}>
          {[listing?.room, listing?.m2 ? `${listing.m2} m²` : null, listing?.floor ? `Kat ${listing.floor}` : null]
            .filter(Boolean)
            .join(" · ")}
        </div>

        {/* Location */}
        {listing?.location && (
          <div className={`text-sm mt-1 ${isDark ? "text-white/60" : "text-black/60"}`}>
            <MapPin className="inline h-3.5 w-3.5 mr-1" />
            {listing.location}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            size="sm"
            variant="solid"
            color="success"
            onClick={() => listingId !== null && onViewDetails(listingId)}
            disabled={listingId === null}
          >
            Tüm Detaylar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const url = listing?.url ? `https://www.emlakjet.com${listing.url}` : null;
              if (url) openExternal(url);
            }}
            disabled={!listing?.url}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MapMarkerPopup;
